import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';

/* eslint-disable-next-line  */
import { apiCommon } from 'src/api/ApiCommon';
import { QuickLinkTrayModelType } from 'src/api/config/quick_link_tray/getQuickLinkTray';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createQuickLinkTray';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import { SdkStaff, SportsListType } from 'src/AppState/sdk/staff';

import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { convertToNumber, validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';

type QuickLinkTrayBodyType = ParamsType['body'];

export interface QuickLinkTrayType {
    sport: string;
    label: string;
    is_active: boolean;
    redirect_option: string;
    direct_url: string;
    event_id: number | null;
    competition_id: number | null;
    casino_game_id: number | null;
}

interface QuickLinkTrayLoadingType {
    type: 'loading';
}

interface QuickLinkTrayReadyType {
    type: 'ready';
    value: Array<QuickLinkTrayModelType>;
}

interface QuickLinkTrayErrorType {
    type: 'error';
    value: string;
}

type QuickLinkTrayContentResponseType = QuickLinkTrayLoadingType | QuickLinkTrayReadyType | QuickLinkTrayErrorType;

interface SportListOptionType {
    value: string;
    label: string;
}

export interface CompetitionSportType {
    competitionId: number | null;
    sport: string;
}

export class QuickLinkTrayV2State {
    @observable private readonly quickLinkTrayListResource: Resource<Array<QuickLinkTrayModelType>>;
    @observable private readonly allSports: Resource<SportsListType>;
    @observable public isEditMode: boolean = false;
    @observable private inEditQuickLinkTray: QuickLinkTrayModelType | null = null;
    @observable public errorMessageForList: string | null = null;

    public readonly sport: FormInputState<string, string>;
    public readonly label: FormInputState<string, string>;

    public isActive: FormInputState<boolean, boolean>;

    public readonly redirectOption: FormInputState<string, string>;
    public readonly directUrl: FormInputState<string, string>;

    // public readonly eventId: FormInputState<string>;
    public readonly eventId: FormInputState<string, number>;

    public readonly competitionId: FormInputState<string, number>;

    public readonly casinoGameId: FormInputState<string, number>;

    private readonly form: FormModel<QuickLinkTrayType>;
    public readonly competitionSport: FormModel<CompetitionSportType>;

    public uploadIcon: UploadImageState;

    public constructor(private readonly sdkStaff: SdkStaff) {
        this.quickLinkTrayListResource = new Resource(async (): Promise<Array<QuickLinkTrayModelType>> => {
            return await apiCommon.getQuickLinkTray.run({});
        });

        this.allSports = new Resource(async (): Promise<SportsListType> => {
            return await this.sdkStaff.getAllSports();
        });

        this.sport = FormInputState.new('').map(validateNotEmpty('This field cant be empty'));

        this.label = FormInputState.new('').map(validateNotEmpty('This field cant be empty'));

        this.isActive = FormInputState.new<boolean>(false);

        this.redirectOption = FormInputState.new('sport');

        this.directUrl = FormInputState.new('').map(validateNotEmpty('This field cant be empty'));

        this.eventId = FormInputState.new('').map(convertToNumber('Not a number'));
        this.competitionId = FormInputState.new('').map(convertToNumber('Not a number'));

        this.casinoGameId = FormInputState.new('').map(convertToNumber('Not a number'));

        this.form = FormModel.group({
            sport: this.sport,
            label: this.label,
            is_active: this.isActive,
            redirect_option: this.redirectOption,
            direct_url: this.directUrl,
            event_id: this.eventId,
            competition_id: this.competitionId,
            casino_game_id: this.casinoGameId,
        });

        this.competitionSport = FormModel.group({
            competitionId: this.competitionId,
            sport: this.sport,
        });

        this.uploadIcon = new UploadImageState('quick-link-tray/icon');
        makeAutoObservable(this);
    }

    @action public resetForm = (): void => {
        this.form.reset();
        this.isEditMode = false;
        this.inEditQuickLinkTray = null;
        this.uploadIcon.handleResetImage();
    };

    @action private handleCreateApiCall = async (quickLinkTray: QuickLinkTrayBodyType): Promise<number | null> => {
        try {
            return await apiCommon.postCreateQuickLinkTray.run({ data: quickLinkTray });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;
        if (result.value.type === 'ok') {
            const topElem = this.quickLinkTraySorted[0] ?? null;
            const display_order =
                this.isEditMode && this.inEditQuickLinkTray !== null
                    ? this.inEditQuickLinkTray.display_order
                    : topElem === null
                      ? 1
                      : topElem.display_order + 1;

            const body = {
                ...result.value.data,
                icon: this.uploadIcon.imgUrl,
                display_order: display_order,
            };

            const submitResult = await this.handleCreateApiCall(body);

            if (submitResult === null) {
                console.log('Error');
                await this.handleError('Something went wrong.');
            } else {
                await this.quickLinkTrayListResource.refresh();
                this.resetForm();
            }
        }
    };

    @action public handleReset = async (): Promise<void> => {
        if (this.isEditMode && this.inEditQuickLinkTray !== null) {
            try {
                await this.setEditQuickLinkTray(this.inEditQuickLinkTray);
            } catch (e) {
                console.error(e);
            }
        } else {
            this.resetForm();
        }
    };

    @action public setEditQuickLinkTray = async (quickLinkTray: QuickLinkTrayModelType): Promise<void> => {
        const event_id = quickLinkTray.event_id ?? '';
        const competition_id = quickLinkTray.competition_id ?? '';
        const casino_game_id = quickLinkTray.casino_game_id ?? '';
        const direct_url = quickLinkTray.direct_url ?? '';
        this.inEditQuickLinkTray = quickLinkTray;
        this.sport.setValue(quickLinkTray.sport);
        this.label.setValue(quickLinkTray.label);
        this.redirectOption.setValue(quickLinkTray.redirect_option);
        this.eventId.setValue(event_id.toString());
        this.directUrl.setValue(direct_url);
        this.competitionId.setValue(competition_id.toString());
        this.casinoGameId.setValue(casino_game_id.toString());
        this.isActive.setValue(quickLinkTray.is_active);
        this.uploadIcon.handleChooseImage(quickLinkTray.icon);
    };

    @action public handleEdit = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;
        if (result.value.type === 'ok' && this.inEditQuickLinkTray !== null) {
            const payload = {
                ...result.value.data,
                icon: this.uploadIcon.imgUrl,
                display_order: this.inEditQuickLinkTray.display_order,
            };

            const submitResult = await this.handleEditApiCall(payload, this.inEditQuickLinkTray.id);

            if (submitResult === null) {
                console.log('Error');
            } else {
                await this.quickLinkTrayListResource.refresh();
                this.resetForm();
            }
        }
    };

    @action private handleEditApiCall = async (
        quickLinkTray: QuickLinkTrayBodyType,
        id: number
    ): Promise<number | null> => {
        try {
            return await apiCommon.putUpdateQuickLinkTray.run({ data: quickLinkTray, id });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @computed private get quickLinkTrayListResponse(): QuickLinkTrayContentResponseType {
        const quickLinkTray = this.quickLinkTrayListResource.get();
        if (quickLinkTray.type === 'ready') {
            return {
                type: 'ready',
                value: quickLinkTray.value,
            };
        }
        if (quickLinkTray.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching quick link tray',
        };
    }

    @computed public get quickLinkTrayList(): Array<QuickLinkTrayModelType> {
        if (this.quickLinkTrayListResponse.type === 'ready') {
            return this.quickLinkTrayListResponse.value;
        }
        return [];
    }

    @computed public get isLoading(): boolean {
        return this.quickLinkTrayListResponse.type === 'loading';
    }

    @action public setEditMode = (value: boolean): void => {
        this.isEditMode = value;
    };

    @action public setDefaultLabel = (value: string): void => {
        const sportId = value;
        const find = this.sportList.find((elem) => elem.id === sportId) ?? null;
        if (find !== null) {
            this.label.setValue(find.name);
        }
    };

    @action public resetLabel = (): void => {
        this.label.setValue('');
    };

    @action public handleRemoveApiCall = async (id: number): Promise<void> => {
        await apiCommon.deleteQuickLinkTray.run({ id });
        await this.quickLinkTrayListResource.refresh();
    };

    @action public setActiveFlag = async (quickLinkTray: QuickLinkTrayModelType): Promise<void> => {
        const payload: QuickLinkTrayModelType = {
            ...quickLinkTray,
            is_active: !quickLinkTray.is_active,
        };

        await this.handleEditActiveFlags(payload);
    };

    @action private handleEditActiveFlags = async (payload: QuickLinkTrayModelType): Promise<void> => {
        const submitResult = await this.handleEditApiCall(payload, payload.id);

        if (submitResult === null) {
            console.log('Error');
        } else {
            await this.quickLinkTrayListResource.refresh();
            this.resetForm();
        }
    };

    @computed public get quickLinkTraySorted(): Array<QuickLinkTrayModelType> {
        return this.quickLinkTrayList.sort((quickLink1: QuickLinkTrayModelType, quickLink2: QuickLinkTrayModelType) => {
            if (quickLink1.display_order > quickLink2.display_order) {
                return -1;
            }
            if (quickLink1.display_order < quickLink2.display_order) {
                return 1;
            }
            return 0;
        });
    }

    @computed private get sportList(): SportsListType {
        const allSports = this.allSports.get();
        if (allSports.type === 'ready') {
            return allSports.value;
        }
        return [];
    }

    @computed public get sportListOptions(): Array<SportListOptionType> {
        const temp = this.sportList.map((elem) => {
            return {
                value: elem.id,
                label: elem.name,
            };
        });

        return [
            {
                value: '',
                label: 'Select sport',
            },
            ...temp,
        ];
    }

    public get redirectOptions(): Array<SportListOptionType> {
        return [
            {
                value: 'direct-link',
                label: 'Direct link',
            },
            {
                value: 'event-id',
                label: 'Event id',
            },
            {
                value: 'sport',
                label: 'Sport',
            },
            {
                value: 'competition-id',
                label: 'Competition id',
            },
            {
                value: 'casino-game-id',
                label: 'Casino game id',
            },
        ];
    }

    public moveItemUp = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'up',
        } as const;
        try {
            const result = await apiCommon.postOrderQuickLinkTray.run({ data });

            if (result >= 0) {
                await this.quickLinkTrayListResource.refresh();
                this.resetForm();
            } else {
                await this.handleError('Can not move item up.');
            }
        } catch (error) {
            await this.handleError('Can not move item up.');
        }
    };

    public moveItemDown = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'down',
        } as const;

        try {
            const result = await apiCommon.postOrderQuickLinkTray.run({ data });

            if (result >= 0) {
                await this.quickLinkTrayListResource.refresh();
                this.resetForm();
            } else {
                await this.handleError('Can not move item down.');
            }
        } catch (error) {
            await this.handleError('Can not move item down.');
        }
    };

    @action public handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await this.wait(3000);
        this.errorMessageForList = null;
    };

    public wait = async (time: number): Promise<void> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    };
}
